import Skeleton from '../Skeleton';
import HomeCard from '../../dashboard/home/HomeCard';
import { HOME_DONUT_SIZE } from '../../dashboard/home/homeTokens';

/**
 * Loading-skeleton atoms for the KAN-946 home dashboards (admin + billing).
 *
 * The skeleton does not copy the cards' look, it BORROWS their layout: it renders the real
 * `HomeCard` shell (white, radius 14, padding 24/18, 32px header row) and the real
 * `cvx-home-*` layout classes (loaded once from the app entry — see homeStyles.js), filled
 * with `Skeleton` bars. So a spacing or size change in homeDashboard.css / homeBands.css
 * moves the skeleton with it; only the SHAPES below approximate each card's content.
 * `homeSkeleton.classNames.test.js` fails if a class used here stops existing in the CSS.
 *
 * Surfaces stay white (skeleton parity rule): ring holes use the card's own surface
 * variable and "outline" cells replace the real cards' soft grey fills.
 */

const SURFACE = 'var(--cvx-home-surface)';

/** 'phone' (<768) | 'tablet' (768-1023) | 'web'. Callers may pass BOTH flags (billing does at tablet): tablet wins. */
export const skeletonHomeLayout = (isMobile, isTablet) => {
  if (isTablet) return 'tablet';
  return isMobile ? 'phone' : 'web';
};

/** One bar centred in a fixed-height line, so a row's text block matches the real line heights. */
export function Bar({ width, height = 11, line, radius, style }) {
  const bar = <Skeleton width={width} height={height} borderRadius={radius ?? Math.ceil(height / 2)} style={style} />;
  if (!line) return bar;
  return <span style={{ display: 'flex', alignItems: 'center', height: line, minWidth: 0 }}>{bar}</span>;
}

/** Header row: 32px icon tile + title bar (+ an optional pill and link bar at the right, in the real `.cvx-home-actions` box). Never wraps — bars have no text to protect, so the title bar gives way instead. */
export function SkelHead({ titleWidth = 120, action, pill }) {
  return (
    <div className="cvx-home-head" style={{ flexWrap: 'nowrap' }} aria-hidden="true">
      <Skeleton width={32} height={32} borderRadius={10} />
      <Skeleton width={titleWidth} height={14} borderRadius={7} style={{ flexShrink: 1, minWidth: 40 }} />
      {action || pill ? (
        <span className="cvx-home-actions">
          {pill ? <Skeleton width={pill} height={22} borderRadius={999} style={{ flexShrink: 1, minWidth: 24 }} /> : null}
          {action ? <Skeleton width={action} height={14} borderRadius={7} /> : null}
        </span>
      ) : null}
    </div>
  );
}

/** The real HomeCard shell with a skeleton header (`head`) and a skeleton body. */
export function SkelCard({ layout, span, stretch = false, className, testId, head = true, titleWidth, action, pill, children }) {
  return (
    <HomeCard layout={layout} span={span} stretch={stretch} className={className} testId={testId}>
      {head ? <SkelHead titleWidth={titleWidth} action={action} pill={pill} /> : null}
      {children}
    </HomeCard>
  );
}

/** HomeFigure: big value (+ optional pill) over a caption; `inline` puts the caption beside it (captionInline). */
export function SkelFigure({ layout, pill = false, inline = false }) {
  const big = 35; // the 32px hero figure x 1.1 line (figureType.js), every layout
  return (
    <div className={inline ? 'cvx-home-num cvx-home-num--inline' : 'cvx-home-num'} aria-hidden="true">
      <div className="cvx-home-num__v" style={{ height: big }}>
        <Skeleton width={layout === 'phone' ? 88 : 104} height={big - 10} borderRadius={9} />
        {pill ? <Skeleton width={64} height={22} borderRadius={999} /> : null}
      </div>
      <Bar width={116} height={11} line={18} />
    </div>
  );
}

/** HomeDonut: a ring (same outer radius + ring width as donutGeometry) with two centre bars. */
export function SkelDonut({ size }) {
  const ring = Math.round(size * 0.113);
  const disc = size - 10;
  const hole = disc - ring * 2;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }} aria-hidden="true">
      <Skeleton width={disc} height={disc} borderRadius="50%" style={{ position: 'absolute', top: 5, left: 5 }} />
      <span
        style={{
          position: 'absolute', top: 5 + ring, left: 5 + ring, width: hole, height: hole, borderRadius: '50%', background: SURFACE,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}
      >
        <Skeleton width={Math.round(size * 0.42)} height={14} borderRadius={7} />
        <Skeleton width={Math.round(size * 0.32)} height={8} borderRadius={4} />
      </span>
    </div>
  );
}

/** HomeLegend: swatch + label + value per line (`lines` = the ruled 40px variant; `sub` = a small note under the value, the 52px row). */
export function SkelLegend({ rows = 4, lines = false, sub = false }) {
  return (
    <div className={`cvx-home-leg${lines ? ' cvx-home-leg--lines' : ''}`} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="cvx-home-leg__row" key={i}>
          <Skeleton width={10} height={10} borderRadius={3} />
          <Skeleton width={`${46 + (i % 3) * 12}%`} height={11} borderRadius={6} />
          {sub ? (
            <span className="cvx-home-leg__vals">
              <Skeleton width={52} height={11} borderRadius={6} />
              <span className="cvx-home-leg__sub"><Skeleton width={44} height={9} borderRadius={5} /></span>
            </span>
          ) : <Skeleton width={52} height={11} borderRadius={6} />}
        </div>
      ))}
    </div>
  );
}

/** The pipeline card's button row: 2-up (40px web / 44px touch) while both fit, one per line on a phone
 *  or in a card too narrow for two (the real row wraps on its buttons' text width; the placeholders
 *  stand in with a ~160px basis so the row wraps at about the same card width). */
export function SkelButtons({ layout, count = 2, pin = false }) {
  const stacked = layout === 'phone';
  return (
    <div
      className={`cvx-home-btnrow${stacked ? ' cvx-home-btnrow--stack' : ''}${pin ? ' cvx-home-btnrow--pin' : ''}`}
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          key={i}
          width="100%"
          height={layout === 'web' ? 40 : 44}
          borderRadius={12}
          style={stacked ? { flex: '1 1 100%' } : { flex: '1 1 0', minWidth: 160 }}
        />
      ))}
    </div>
  );
}

/** One list row (HomeListRow): 36px lead, two text lines, trailing value + sub. 56px (60 phone). */
function SkelRow({ i, square, trailing }) {
  return (
    <div className="cvx-home-row">
      <Skeleton width={36} height={36} borderRadius={square ? 11 : '50%'} />
      <span className="cvx-home-row__tx">
        <Bar width={`${46 + (i % 3) * 14}%`} height={12} line={18} />
        <Bar width={`${30 + (i % 2) * 14}%`} height={10} line={16} />
      </span>
      {trailing ? (
        <span className="cvx-home-row__tr">
          <Bar width={56} height={12} line={18} />
          <Bar width={38} height={10} line={16} />
        </span>
      ) : null}
    </div>
  );
}

/** A HomeList of `n` skeleton rows (shares spare height in a `stretch` card exactly like the real list). */
export function SkelRows({ n = 4, square = false, trailing = true }) {
  return (
    <div className="cvx-home-list" style={{ '--rows': n }} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => <SkelRow key={i} i={i} square={square} trailing={trailing} />)}
    </div>
  );
}

/** HomeGroupLabel placeholder (the 20px uppercase day label). */
export function SkelGroupLabel() {
  return (
    <div className="cvx-home-group" aria-hidden="true">
      <Skeleton width={64} height={9} borderRadius={5} />
    </div>
  );
}

/** The stops hero line (HomeStopsHero): the big figure + caption on one baseline, a pill at the right. Same boxes as the real line. */
export function SkelStopsHero({ layout }) {
  const big = 35; // the 32px hero figure x 1.1 line (figureType.js), every layout
  return (
    <div className="cvx-home-shero" aria-hidden="true">
      <div className="cvx-home-shero__fig" style={{ alignItems: 'center' }}>
        <Skeleton width={layout === 'phone' ? 72 : 84} height={big - 8} borderRadius={9} />
        <Skeleton width={64} height={11} borderRadius={6} />
      </div>
      <Skeleton width={150} height={28} borderRadius={999} style={{ flexShrink: 1, minWidth: 60 }} />
    </div>
  );
}

/** The stop bar (HomeStopBar): one slate capsule at the real bar's height (8px on every layout). */
export function SkelStopBar() {
  return <Skeleton width="100%" height={8} borderRadius={999} />;
}

/** CrewRows: `count` row placeholders (avatar, name over status line, count), same boxes as the real compact rows. */
export function SkelCrewRows({ count = 3 }) {
  return (
    <div className="cvx-home-crew" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div className="cvx-home-crow cvx-home-crow--skel" key={i}>
          <span className="cvx-home-crow__av"><Skeleton width="100%" height="100%" borderRadius="50%" /></span>
          <span className="cvx-home-crow__body">
            <span className="cvx-home-crow__top">
              <span className="cvx-home-crow__name"><Bar width={`${52 - (i % 3) * 8}%`} height={13} line={20} /></span>
              <Bar width={28} height={13} line={20} />
            </span>
            <Bar width={`${64 - (i % 2) * 14}%`} height={11} line={18} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** TodayCard body: the hero line + stop bar, then the crew rows (one column everywhere; two only on a full-width card — all CSS). */
export function SkelTodayBody({ layout }) {
  return (
    <div className="cvx-home-today">
      <div className="cvx-home-today__head">
        <SkelStopsHero layout={layout} />
        <SkelStopBar layout={layout} />
      </div>
      <SkelCrewRows count={layout === 'phone' ? 3 : 4} />
    </div>
  );
}

/** PipelineCard body for its three layouts: narrow (web, half width) · tablet · phone. */
export function SkelPipelineBody({ variant }) {
  const size = HOME_DONUT_SIZE[variant] || HOME_DONUT_SIZE.narrow;
  const layout = variant === 'narrow' ? 'web' : variant;
  return (
    <div className="cvx-home-pbody">
      <div className="cvx-home-fill">
        <SkelDonut size={size} />
        <SkelLegend lines />
      </div>
      <SkelButtons layout={layout} pin={layout !== 'phone'} />
    </div>
  );
}
